import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Box, Button, Card, CardContent, CircularProgress, Divider, Stack, Typography } from "@mui/material";
import { ShoppingCart as ShoppingCartIcon } from "@mui/icons-material";
import api from "../../../api";
import { useConfirmPurchase } from "./ConfirmPurchaseDialog";

/**
 * Dashboard shortcut to buy more practice without leaving the panel: the two packs,
 * plus links into the Mock Exams and Past Papers pages where single items are sold.
 *
 * Not shown to students whose plan already includes everything.
 */
export default function DashboardShopCard({ studentPrefix }) {
  const [packs, setPacks] = useState(null);
  const [buyingId, setBuyingId] = useState(null);
  const { requestPurchase, purchaseDialog } = useConfirmPurchase();

  useEffect(() => {
    let cancelled = false;
    api
      .get("/bundles")
      .then((res) => {
        if (!cancelled) setPacks(res.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setPacks([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (packs === null) return null;
  // The same flag is set on every pack: a plan covers the whole library.
  if (packs.some((p) => p.covered_by_plan)) return null;

  const offers = packs.filter((p) => !p.already_owned);

  const buy = (pack) => {
    setBuyingId(pack.id);
    requestPurchase("bundle", pack.id, {
      name: pack.name,
      price: pack.price_display || null,
      onCancel: () => setBuyingId(null),
    });
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: "#111827", display: "flex", alignItems: "center", gap: 1 }}>
          <ShoppingCartIcon sx={{ color: "#7c3aed" }} /> Get more practice
        </Typography>
      </Box>
      <Card
        sx={{
          borderRadius: "28px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
          mb: 3,
          border: "1px solid #f0f0f0",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: "16px !important" }}>
          {offers.map((pack, idx) => (
            <Box key={pack.id}>
              {idx > 0 ? <Divider sx={{ my: 1.25 }} /> : null}
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>{pack.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    All {pack.item_count} · {pack.price_display}
                    {pack.access_months ? ` for ${pack.access_months} months` : ""}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  disabled={buyingId === pack.id}
                  onClick={() => buy(pack)}
                  startIcon={buyingId === pack.id ? <CircularProgress size={12} color="inherit" /> : null}
                  sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#7c3aed", "&:hover": { bgcolor: "#6d28d9" }, flexShrink: 0 }}
                >
                  {buyingId === pack.id ? "Opening…" : "Buy"}
                </Button>
              </Stack>
            </Box>
          ))}

          {offers.length > 0 ? <Divider sx={{ my: 1.5 }} /> : null}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
            Or buy single papers — no need to leave the panel.
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              component={Link}
              to={`/${studentPrefix}/mock-exams?tab=exam`}
              size="small"
              variant="outlined"
              sx={{ textTransform: "none", fontWeight: 700, flex: 1 }}
            >
              Mock exams
            </Button>
            <Button
              component={Link}
              to={`/${studentPrefix}/past-papers`}
              size="small"
              variant="outlined"
              sx={{ textTransform: "none", fontWeight: 700, flex: 1 }}
            >
              Practice papers
            </Button>
          </Stack>
        </CardContent>
      </Card>
      {purchaseDialog}
    </>
  );
}
